import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo,
} from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { Menu } from '@headlessui/react';

import { Icon, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';
import { Button } from 'components/form';
import ProfilePics from 'components/candidate/ProfilePics';
import EventCard from 'components/ComponentV2/EventCard';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { SocietyTabsType } from 'hooks/useCampaigns.hook';
import { useSocietyProfile } from 'pages/society-profile/UseSocietyProfile.hook';
import { useAuth } from 'lib/providers/AuthProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import { parseResponse } from 'lib/utils/parser';
import { TouchpointType } from 'lib/models/touchpoint';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { EventTouchpointable } from 'lib/models/event';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import SocietyEventsTable from '../SocietyEventsTable';
import PendingInviteCard from '../../../components/PendingInviteCard';

import styles from './SocietyEventsListing.module.scss';

export const SocietyEventsListing: React.FC = memo(() => {
  const router = useRouter();
  const { isManager } = useAuth();

  const { societyDashboard, mutateSocietyDashboard, similarEvents } =
    useSocietyProfile();

  const scrollTabRef = useRef<HTMLDivElement | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const { setSelectedEventState } = useCampaigns();

  useEffect(() => {
    if (!isManager) {
      router.replace(PAGE_ROUTES.SIGN_IN_SOCIETY);
    }
  }, [isManager]);

  const {
    live_touchpoint_applications_count = 0,
    live_events_touchpoint_applications_avatar_url,
    live_touchpoints_count,
    total_count,
    closed_touchpoint_count,
    touchpoints_with_live_application,
  } = societyDashboard || {};

  const event_registrations_avatar_urls = useMemo(() => {
    return live_events_touchpoint_applications_avatar_url
      ?.filter((item) => item.avatar_url !== null)
      ?.map((item) => item.avatar_url);
  }, [live_events_touchpoint_applications_avatar_url]);

  const liveEventsList = useMemo(() => {
    return touchpoints_with_live_application?.map(({ id, title }) => {
      return (
        <button
          key={id}
          onClick={() => {
            router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}`);
          }}
        >
          <span>{title}</span>
        </button>
      );
    });
  }, [touchpoints_with_live_application]);

  const getViewCTA = useCallback(() => {
    return touchpoints_with_live_application ? (
      <Menu as="div" className={styles.reviewMenu}>
        <Menu.Button className={cn(styles.trigger)}>
          <Row align="center" justify="center" columnGap={8}>
            View
            <Icon iconName="icon_arrow-down" size="xsmall" />
          </Row>
        </Menu.Button>
        <Menu.Items as="ul" className={styles.touchpoint}>
          <Menu.Item as="li" className={styles.item}>
            {liveEventsList}
          </Menu.Item>
        </Menu.Items>
      </Menu>
    ) : (
      <></>
    );
  }, [touchpoints_with_live_application]);

  const onHeaderShow = useCallback(() => {
    setHeaderScrolled(false);
  }, []);

  const onHeaderHide = useCallback(() => {
    setHeaderScrolled(true);
  }, []);

  useVisibleOnIntersect({
    elRef: headerRef,
    threshold: 1,
    onIntersect: onHeaderShow,
    onHidden: onHeaderHide,
    rootMargin: '0px',
    isEqualToThreshold: true,
  });

  const similarEventList = useMemo(() => {
    return similarEvents?.map(
      ({
        id,
        title,
        slug,
        touchpointable_type,
        start_date,
        avatar_urls,
        applications,
        touchpointable: touchpointableResponse,
      }) => {
        const url = getOpportunityPublicPagePathname(touchpointable_type, slug);
        const touchpointable = parseResponse(
          touchpointableResponse as EventTouchpointable
        );

        return (
          <div key={id} className={styles.similarlistCard}>
            <EventCard
              key={id}
              title={title}
              url={url}
              start_date={start_date}
              avatar_urls={avatar_urls}
              applications={applications}
              icon_url={touchpointable?.icon_url}
              className={styles.eventcard}
              onNewTab={true}
              registrationsDesc={' students already registered to this event.'}
              titleClassname={styles.eventcardTitle}
              dateClassname={styles.eventcardDate}
              application_deadline={''}
              cities={null}
              cohost_applications={null}
              showDetails={false}
            />
          </div>
        );
      }
    );
  }, [similarEvents]);

  useEffect(() => {
    return () => {
      scrollTabRef.current = null;
    };
  }, []);

  return (
    <section
      className={styles.SocietyEventsListing}
      data-testid="SocietyEventsListing"
    >
      {headerScrolled ? null : (
        <Row
          align="center"
          columnGap={4}
          className={styles.redirect}
          onClick={() => router.push(PAGE_ROUTES.DASHBOARD)}
        >
          <Icon iconName="chevron-left" size="large" />
          Dashboard
        </Row>
      )}
      <div
        ref={headerRef}
        className={cn({
          [styles.header]: true,
          [styles.headerScrolled]: headerScrolled,
        })}
      >
        <p className={styles.title}>Events</p>
        <ButtonV3
          color={total_count === 0 ? 'secondary' : 'primary'}
          onClick={() =>
            router.push(
              `${PAGE_ROUTES.EVENTS_CREATE}?step=1&type=${TouchpointType.Event}`
            )
          }
          className={cn({
            [styles.whitebutton]: total_count === 0,
          })}
        >
          Create new
        </ButtonV3>
      </div>
      {headerScrolled || !societyDashboard ? null : (
        <p className={styles.totalcount}>{total_count} Events</p>
      )}
      {!societyDashboard ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.container}>
          <PendingInviteCard className={styles.pendinginvitecard} />
          {total_count === 0 ? (
            <div className={styles.noResults}>
              <div className={styles.noResultsContent}>
                <Icon iconName="desert" size="auto" />
                <div className={styles.noResultsContentText}>
                  <p>Host your first event</p>
                  <span>
                    Looking to engage with students? Host an event, promote it
                    and get registrations quickly.
                  </span>
                  <br />
                  <br />
                  <span>
                    Message societies to partner with them, once the event is
                    posted you can invite them as co-hosts.
                  </span>
                </div>
                <Row columnGap={8}>
                  <ButtonV3
                    onClick={() =>
                      router.push(
                        `${PAGE_ROUTES.EVENTS_CREATE}?step=1&type=${TouchpointType.Event}`
                      )
                    }
                    className={styles.noResultsContentButton}
                  >
                    Host your event
                  </ButtonV3>
                  <ButtonV3
                    color="secondary"
                    onClick={() => router.push(PAGE_ROUTES.STUDENT_SOCIETIES)}
                    className={styles.noResultsContentButton}
                    borderless
                  >
                    Message societies
                  </ButtonV3>
                </Row>
              </div>
              <div className={styles.separator}></div>
              <p className={styles.similarlistTitle}>
                Here are a few live events from societies, check them out!
                <br />
                <br />
                Get involved by messaging the society, they can add you as a
                cohost.
              </p>
              <div className={styles.similarlist}>{similarEventList}</div>
              <ButtonV3
                onClick={() =>
                  router.push(
                    `${PAGE_ROUTES.EVENTS_CREATE}?step=1&type=${TouchpointType.Event}`
                  )
                }
                className={styles.similarlistButton}
                isFullWidth
              >
                Create new
              </ButtonV3>
            </div>
          ) : (
            <>
              <section className={styles.island1}>
                {live_touchpoint_applications_count > 0 ? (
                  <div
                    className={cn(styles.island1Div, styles.island1DivActive)}
                  >
                    <div>
                      <p className={styles.island1Title}>Registrations</p>
                      <p className={styles.island1Count}>
                        {live_touchpoint_applications_count}
                      </p>
                    </div>
                    {live_touchpoint_applications_count > 0 ? (
                      <div>
                        {live_touchpoint_applications_count > 2 ? (
                          <Row
                            columnGap={12}
                            className={cn(styles.subscriberRow)}
                          >
                            <ProfilePics
                              maxPicsToShow={3}
                              urls={event_registrations_avatar_urls || []}
                            />
                          </Row>
                        ) : null}
                        {getViewCTA()}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className={styles.island1Div}>
                  <div>
                    <p className={styles.island1Title}>Live Events</p>
                    <p className={styles.island1Count}>
                      {live_touchpoints_count}
                    </p>
                  </div>
                  <Row columnGap={10} isFullWidthRow>
                    <Button
                      color="white"
                      className={cn(styles.whitebutton, styles.flex)}
                      onClick={() => {
                        setSelectedEventState(SocietyTabsType.LIVE);
                        scrollTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                      disabled={live_touchpoints_count === 0}
                    >
                      View
                    </Button>
                    <Button
                      color="white"
                      className={cn(styles.whitebutton, styles.flex)}
                      onClick={() =>
                        router.push(
                          `${PAGE_ROUTES.EVENTS_CREATE}?step=1&type=${TouchpointType.Event}`
                        )
                      }
                    >
                      Add new
                    </Button>
                  </Row>
                </div>
                <div className={styles.island1Div}>
                  <div>
                    <p className={styles.island1Title}>Ended events</p>
                    <p className={styles.island1Count}>
                      {closed_touchpoint_count}
                    </p>
                  </div>
                  {closed_touchpoint_count ? (
                    <Button
                      color="white"
                      isFullWidth
                      className={styles.whitebutton}
                      onClick={() => {
                        setSelectedEventState(SocietyTabsType.ENDED);
                        scrollTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                      disabled={closed_touchpoint_count === 0}
                    >
                      View
                    </Button>
                  ) : null}
                </div>
              </section>

              <div ref={scrollTabRef}>
                <SocietyEventsTable
                  societyDashboard={societyDashboard}
                  mutateSocietyDashboard={mutateSocietyDashboard}
                />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
});

SocietyEventsListing.displayName = 'SocietyEventsListing';
